import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Database, Group, Player, Tournament, Match, Club } from '../types';
import { migrateData } from '../utils/helpers';
import { checkChallengeDeadlines } from '../utils/challengeHelpers';
import { collection, doc, getDoc, getDocs, deleteDoc, query, where, onSnapshot, or, orderBy, limit, documentId } from 'firebase/firestore';

import { db as firestore } from '../firebaseConfig';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FirestoreTitleDefinition } from '../types/titles';
import { useTranslation } from 'react-i18next';

export enum View {
  GROUP_SELECT = 'GROUP_SELECT',
  HOME = 'HOME',
  TOURNAMENTS = 'TOURNAMENTS',
  TOURNAMENT_DETAIL = 'TOURNAMENT_DETAIL',
  PLAYERS = 'PLAYERS',
  PLAYER_DETAIL = 'PLAYER_DETAIL',
  CLUBS = 'CLUBS',
  H2H = 'H2H',
  BRACKET = 'BRACKET',
  KOTC_DETAIL = 'KOTC_DETAIL',
  CREATE_PLAYER = 'CREATE_PLAYER',
  CREATE_TOURNAMENT = 'CREATE_TOURNAMENT',
  CREATE_TEAM = 'CREATE_TEAM',
  CREATE_MATCH = 'CREATE_MATCH',
  MATCHMAKING = 'MATCHMAKING',
  PROFILE = 'PROFILE',
  INBOX = 'INBOX',
  CHALLENGES = 'CHALLENGES',
  RULES = 'RULES',
  ADMIN = 'ADMIN',
  DASHBOARD = 'DASHBOARD',
  FRIENDS = 'FRIENDS',
  GLOBAL_MATCHES = 'GLOBAL_MATCHES',
  GLOBAL_TOURNAMENTS = 'GLOBAL_TOURNAMENTS',
  SETTINGS = 'SETTINGS',
  FULL_FEED = 'FULL_FEED',
  EDIT_GROUP = 'EDIT_GROUP',
  GROUP_ADMIN = 'GROUP_ADMIN',
  GROUP_CHAT = 'GROUP_CHAT',
  SUBSCRIPTION = 'SUBSCRIPTION',
  SCORE = 'SCORE',
  HUB = 'HUB'
}

interface AppContextProps {
  db: Database;
  view: View; // Kept for compatibility, derived from location
  currentGroup: Group | null;
  setCurrentGroup: (id: string | null) => void;
  setDb: React.Dispatch<React.SetStateAction<Database>>;
  saveData: () => void;
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  contextData: any;
  setContextData: React.Dispatch<React.SetStateAction<any>>;
  goToPage: (view: View, data?: any) => void;
  theme: 'standard' | 'light' | 'pro';
  toggleTheme: (theme: 'standard' | 'light' | 'pro') => void;
  userProfile: Player | null;
  matchToShare: Match | null;
  setMatchToShare: (match: Match | null) => void;
  shareMatch: (match: Match) => void;
  globalRegionOverride: string | null;
  setGlobalRegionOverride: (region: string | null) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const [db, setDb] = useState<Database>({ groups: [], currentGroupId: null });
  const [contextData, setContextData] = useState<any>({});
  const [theme, setTheme] = useState<'standard' | 'light' | 'pro'>('standard');
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [userProfile, setUserProfile] = useState<Player | null>(null);
  const [matchToShare, setMatchToShare] = useState<Match | null>(null);
  const [globalRegionOverride, setGlobalRegionOverride] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const shareMatch = (match: Match) => setMatchToShare(match);

  // Ref to track if the last update came from Firestore (Remote)
  // If true, we skip the next auto-save to prevent infinite loops
  const isRemoteUpdate = React.useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Derive view from location
  const getViewFromPath = (pathname: string): View => {
    if (pathname === '/') return View.HUB;
    if (pathname === '/dashboard') return View.DASHBOARD;
    if (pathname === '/groups') return View.GROUP_SELECT;
    if (pathname === '/profile') return View.PROFILE;
    if (pathname === '/inbox') return View.INBOX;
    if (pathname === '/friends') return View.FRIENDS;
    if (pathname === '/matches') return View.GLOBAL_MATCHES;
    if (pathname === '/my-tournaments') return View.GLOBAL_TOURNAMENTS;
    if (pathname === '/my-tournaments') return View.GLOBAL_TOURNAMENTS;
    if (pathname === '/rules') return View.RULES;
    if (pathname === '/score') return View.SCORE;
    if (pathname === '/hub/my-tournaments') return View.GLOBAL_TOURNAMENTS;
    if (pathname === '/hub/challenge-players') return View.FRIENDS;

    // Group routes
    if (pathname.includes('/tournaments')) return View.TOURNAMENTS;
    if (pathname.includes('/tournament/')) return View.TOURNAMENT_DETAIL; // Specific tournament
    if (pathname.includes('/players')) return View.PLAYERS;
    if (pathname.includes('/player/')) return View.PLAYER_DETAIL;
    if (pathname.includes('/clubs')) return View.CLUBS;
    if (pathname.includes('/h2h')) return View.H2H;
    if (pathname.includes('/kotc/')) return View.KOTC_DETAIL;
    if (pathname.includes('/matchmaking')) return View.MATCHMAKING;
    if (pathname.includes('/challenges')) return View.CHALLENGES;
    if (pathname.includes('/create-tournament')) return View.CREATE_TOURNAMENT;
    if (pathname.includes('/create-player')) return View.CREATE_PLAYER;
    if (pathname.includes('/create-match')) return View.CREATE_MATCH;
    if (pathname.includes('/feed')) return View.FULL_FEED;
    if (pathname.includes('/chat')) return View.GROUP_CHAT;

    // Default to HOME if in a group root
    if (pathname.match(/^\/group\/[^/]+$/)) return View.HOME;

    return View.GROUP_SELECT;
  };

  const view = getViewFromPath(location.pathname);

  // Load initial data from localStorage (for backward compatibility and offline mode)
  useEffect(() => {
    const loadLocalData = () => {
      const stored = localStorage.getItem('padelBrothersDB_v15');
      if (stored) {
        const parsed = JSON.parse(stored);
        const migrated = migrateData(parsed);

        // EXCLUDE stale data from local storage load - these are all managed by real-time listeners
        // - globalConfig: live config listener
        // - groups: live groups listener (stale cache causes ghost groups like Team SES)
        // - currentGroupId: must come from URL only
        // - users: live users listener (stale list causes wrong member counts)
        // - notifications/challenges/friendRequests: live listeners (stale cache causes ghost notifications)
        // - globalClubs: live clubs listener (stale legacy cache persists deleted clubs)
        const { globalConfig, groups, currentGroupId, users, notifications, challenges, friendRequests, globalClubs, ...dbToLoad } = migrated;

        setDb({ ...dbToLoad, groups: [], globalClubs: [], currentGroupId: null });
      }
      setIsLoadingData(false);
    };

    loadLocalData();

    // Load App Theme
    const storedAppTheme = localStorage.getItem('padelAppTheme') as 'standard' | 'light' | 'pro';
    if (storedAppTheme) {
      setTheme(storedAppTheme);
      applyTheme(storedAppTheme);
    } else {
      applyTheme('standard');
    }
  }, []);

  // Sync currentGroupId from URL
  // IMPORTANT: Only depend on location.pathname, NOT db.currentGroupId.
  // Including db.currentGroupId caused a race condition:
  // setCurrentGroup() fires BEFORE navigate(), triggering this effect
  // with the OLD URL, which clears the group back to null → flicker.
  useEffect(() => {
    const match = location.pathname.match(/^\/group\/([^/]+)/);
    if (match && match[1]) {
      const groupIdFromUrl = match[1];
      setDb(prev => {
        if (prev.currentGroupId === groupIdFromUrl) return prev;
        console.log(`Syncing group ID from URL: ${groupIdFromUrl}`);
        return { ...prev, currentGroupId: groupIdFromUrl };
      });
    } else if (!location.pathname.startsWith('/profile/edit')) {
      setDb(prev => {
        if (!prev.currentGroupId) return prev;
        return { ...prev, currentGroupId: null };
      });
    }
  }, [location.pathname]);

  // Sync tournamentId from URL
  useEffect(() => {
    const matchT = location.pathname.match(/\/tournament\/([^/]+)/);
    if (matchT && matchT[1]) {
      const tId = matchT[1];
      // Use functional update to avoid needing contextData in dependencies
      setContextData((prev: any) => {
        if (prev.tournamentId !== tId) {
          console.log(`Syncing tournament ID from URL: ${tId}`);
          return { ...prev, tournamentId: tId };
        }
        return prev; // No change needed
      });
    } else {
      setContextData((prev: any) => {
        if (prev.tournamentId) {
          return { ...prev, tournamentId: null };
        }
        return prev; // No change needed
      });
    }
  }, [location.pathname]); // Only depend on pathname, not contextData

  // ENFORCE CONTEXTUAL BAN
  // If user is in bannedPlayers of the current group or tournament, kick them out immediately
  useEffect(() => {
    if (!user || !db.currentGroupId) return;

    const group = db.groups?.find(g => g.id === db.currentGroupId);
    if (!group) return;

    // 1. Group Ban
    if (group.bannedPlayers?.some(bp => bp.id === user.uid)) {
      console.warn("User is banned from this group. Redirecting...");
      showToast(t('inbox.system.bannedFromGroup'), "error");
      // Clear group and redirect
      setDb(prev => ({ ...prev, currentGroupId: null }));
      navigate('/groups');
      return;
    }

    // 2. Tournament Ban
    const matchTournament = location.pathname.match(/\/tournament\/([^/]+)/);
    if (matchTournament && matchTournament[1]) {
      const tournamentId = matchTournament[1];
      const tournament = group.tournaments?.find(t => t.id === tournamentId);
      if (tournament?.bannedPlayers?.some((bp: any) => bp.id === user.uid)) {
        console.warn("User is banned from this tournament. Redirecting...");
        showToast(t('inbox.system.bannedFromTournament'), "error");
        // Redirect to group home
        navigate(`/group/${group.id}`);
      }
    }
  }, [user, db.currentGroupId, db.groups, location.pathname]);

  // REAL-TIME SYNC: Listen to Groups changes from Firestore (Only user's groups)
  useEffect(() => {
    if (!user) return;

    // Query ONLY groups where user is a member for scalability
    // This dramatically reduces reads and costs at scale
    const q = query(
      collection(firestore, 'groups'),
      where('members', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {

      const remoteGroups: Group[] = [];
      snapshot.forEach(doc => {
        remoteGroups.push({ id: doc.id, ...doc.data() } as Group);
      });

      // Preserve Official Group if it was loaded independently
      setDb(prev => {
        const officialId = 'official_padel_brothers';
        const prevOfficial = prev.groups.find(g => g.id === officialId);
        const newOfficial = remoteGroups.find(g => g.id === officialId);

        if (prevOfficial && !newOfficial) {
          remoteGroups.push(prevOfficial);
        }

        // CRITICAL: Preserve sub-collection data (players, tournaments, quickMatches) from local state
        // The parent document does NOT contain these arrays anymore (Migration V2)
        // Only the sub-collection listeners populate them. If we overwrite, we lose that data.
        const mergedGroups = remoteGroups.map(remoteGroup => {
          const localGroup = prev.groups.find(g => g.id === remoteGroup.id);
          if (localGroup) {
            return {
              ...remoteGroup,
              // ALWAYS prefer localGroup subcollection data if it exists and has items, because remoteGroup data might be a stale legacy array on the parent doc
              players: (localGroup.players && localGroup.players.length > 0) ? localGroup.players : remoteGroup.players,
              tournaments: (localGroup.tournaments && localGroup.tournaments.length > 0) ? localGroup.tournaments : remoteGroup.tournaments,
              quickMatches: (localGroup.quickMatches && localGroup.quickMatches.length > 0) ? localGroup.quickMatches : remoteGroup.quickMatches,
              matchmakingRequests: remoteGroup.matchmakingRequests || [],
            };
          }
          return remoteGroup;
        });

        if (JSON.stringify(prev.groups) !== JSON.stringify(mergedGroups)) {
          return { ...prev, groups: mergedGroups };
        }
        return prev;
      });
    });

    return () => unsubscribe();
  }, [user]);

  // REAL-TIME SYNC: Always listen to OFFICIAL GROUP (independent of membership)
  useEffect(() => {
    if (!user) return;

    const officialDocRef = doc(firestore, 'groups', 'official_padel_brothers');
    const unsubscribe = onSnapshot(officialDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const officialGroup = { id: docSnap.id, ...docSnap.data() } as Group;

        isRemoteUpdate.current = true;
        setDb(prev => {
          // Check if already exists in groups
          const existingIdx = prev.groups.findIndex(g => g.id === 'official_padel_brothers');
          if (existingIdx !== -1) {
            const localGroup = prev.groups[existingIdx];

            // CRITICAL: Preserve sub-collection data (players, tournaments, quickMatches)
            // from local state. The parent document does NOT contain these arrays anymore
            // (Migration V2). Only the sub-collection listeners populate them.
            // If we overwrite with the parent doc snapshot, we lose all sub-collection data
            // and revert to stale legacy arrays (e.g., 72 players instead of 102).
            const mergedGroup = {
              ...officialGroup,
              players: (localGroup.players && localGroup.players.length > 0) ? localGroup.players : officialGroup.players,
              tournaments: (localGroup.tournaments && localGroup.tournaments.length > 0) ? localGroup.tournaments : officialGroup.tournaments,
              quickMatches: (localGroup.quickMatches && localGroup.quickMatches.length > 0) ? localGroup.quickMatches : officialGroup.quickMatches,
              matchmakingRequests: officialGroup.matchmakingRequests || [],
            };

            // Only update if changed
            if (JSON.stringify(prev.groups[existingIdx]) !== JSON.stringify(mergedGroup)) {
              const updatedGroups = [...prev.groups];
              updatedGroups[existingIdx] = mergedGroup;
              return { ...prev, groups: updatedGroups };
            }
            return prev;
          } else {
            // Add new
            return { ...prev, groups: [...prev.groups, officialGroup] };
          }
        });
      }
    });

    return () => unsubscribe();
  }, [user]);



  // REAL-TIME SYNC: Fetch Current Group if NOT in my groups (e.g. visiting a public group)
  useEffect(() => {
    if (!user || !db.currentGroupId) return;

    // specific check: is the current group already loaded?
    const isLoaded = db.groups.some(g => g.id === db.currentGroupId);
    if (isLoaded) return;

    console.log(`Fetching current group (non-member): ${db.currentGroupId}`);
    const groupDocRef = doc(firestore, 'groups', db.currentGroupId);

    const unsubscribe = onSnapshot(groupDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const groupData = { id: docSnap.id, ...docSnap.data() } as Group;

        isRemoteUpdate.current = true;
        setDb(prev => {
          // Double check availability to avoid race conditions
          const exists = prev.groups.some(g => g.id === groupData.id);
          if (exists) {
            // CRITICAL: Preserve sub-collection data when merging
            return {
              ...prev,
              groups: prev.groups.map(g => {
                if (g.id !== groupData.id) return g;
                return {
                  ...groupData,
                  players: (g.players && g.players.length > 0) ? g.players : groupData.players,
                  tournaments: (g.tournaments && g.tournaments.length > 0) ? g.tournaments : groupData.tournaments,
                  quickMatches: (g.quickMatches && g.quickMatches.length > 0) ? g.quickMatches : groupData.quickMatches,
                  matchmakingRequests: groupData.matchmakingRequests || [],
                };
              })
            };
          }
          // Add to groups list specifically for this session
          return { ...prev, groups: [...prev.groups, groupData] };
        });
      }
    }, (error) => {
      console.error("Error fetching current group:", error);
      // If permission error or not found, maybe redirect or show toast?
      // But for public groups it should work due to security rules
    });

    return () => unsubscribe();
  }, [user, db.currentGroupId, db.groups]); // db.groups dependency is important here to stop listening once loaded

  // REAL-TIME SYNC: Listen to Global Clubs collection (independent from groups)
  useEffect(() => {
    if (!user) return;

    const clubsRef = collection(firestore, 'clubs');
    const unsubClubs = onSnapshot(clubsRef, (snapshot) => {
      const clubsList: Club[] = [];
      snapshot.forEach(docSnap => {
        clubsList.push({ id: docSnap.id, ...docSnap.data() } as Club);
      });

      // One-time migration: add country:'FR' to clubs that lack it
      const clubsWithoutCountry = clubsList.filter(c => !c.country);
      if (clubsWithoutCountry.length > 0) {
        console.log(`[Migration] Adding country=FR to ${clubsWithoutCountry.length} clubs...`);
        import('firebase/firestore').then(({ updateDoc, doc: firestoreDoc }) => {
          clubsWithoutCountry.forEach(club => {
            updateDoc(firestoreDoc(firestore, 'clubs', club.id), { country: 'FR' })
              .catch(err => console.warn(`[Migration] Failed to update club ${club.id}:`, err));
          });
        });
      }

      isRemoteUpdate.current = true;
      setDb(prev => ({ ...prev, globalClubs: clubsList }));
    }, (error) => {
      console.error("Error fetching global clubs:", error);
    });

    return () => unsubClubs();
  }, [user]);

  // SYNC: Fetch users collection for player enrichment and ghost/bot filtering
  // Using getDocs (one-time fetch) instead of onSnapshot (continuous listener)
  // to avoid persistent WebSockets & background memory leaks on Android.
  useEffect(() => {
    if (!user) return;

    let isCancelled = false;

    const fetchAllUsers = async () => {
      try {
        const q = collection(firestore, 'users');
        const snapshot = await getDocs(q);

        if (isCancelled) return;

        const usersList: Player[] = [];
        snapshot.forEach(doc => {
          usersList.push({ id: doc.id, ...doc.data() } as Player);
        });

        isRemoteUpdate.current = true;
        setDb(prev => ({ ...prev, users: usersList }));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchAllUsers();

    // Refresh only when tab becomes visible or every 10 minutes
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchAllUsers();
      }
    }, 10 * 60 * 1000);

    const handleVisibility = () => {
      if (!document.hidden) {
        fetchAllUsers();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);


  // REAL-TIME SYNC: Listen to Global Notifications, Challenges, and Friend Requests
  useEffect(() => {
    if (!user) return;

    // 1. Notifications (User Subcollection)
    const notifQuery = query(
      collection(firestore, 'users', user.uid, 'notifications')
    );
    const unsubNotif = onSnapshot(notifQuery, (snapshot) => {
      const notifications: any[] = [];
      snapshot.docChanges().forEach(change => {
        // Skip documents that were removed (server-confirmed deletes)
        if (change.type === 'removed') return;
      });
      snapshot.forEach(docSnap => {
        // Skip ghost documents: if the snapshot is from the server but the doc
        // has pending writes, it means OUR local write (e.g. setDoc from markAsRead fallback)
        // created a "phantom" doc. Filter these out.
        // Also skip if the doc data is essentially empty (only has 'read' field from a ghost setDoc)
        const data = docSnap.data();
        const isGhostDoc = !data.type && !data.message && !data.title && !data.createdAt;
        if (isGhostDoc) {
          // Auto-cleanup: delete the ghost document from Firestore
          console.warn(`[NotifSync] Deleting empty ghost notification: ${docSnap.id}`);
          deleteDoc(docSnap.ref).catch(() => {});
          return;
        }

        // Hardcore blacklist filter: if it was deleted but still comes back from cache
        const blacklisted = JSON.parse(localStorage.getItem('pb_ghost_notifs') || '[]');
        if (blacklisted.includes(docSnap.id)) {
           console.warn(`[NotifSync] Ignoring blacklisted ghost notification: ${docSnap.id}`);
           return;
        }

        notifications.push({ id: docSnap.id, ...data });
      });

      setDb(prev => {
        isRemoteUpdate.current = !snapshot.metadata.hasPendingWrites;
        return { ...prev, notifications };
      });
    });

    // 2. Challenges
    const challengesQuery = query(
      collection(firestore, 'challenges'),
      or(
        where('challengerId', '==', user.uid),
        where('challengedId', '==', user.uid),
        where('challengerPartnerId', '==', user.uid),
        where('challengedPartnerId', '==', user.uid)
      )
    );
    const unsubChallenges = onSnapshot(challengesQuery, (snapshot) => {
      const challenges: any[] = [];
      snapshot.forEach(doc => {
        challenges.push({ id: doc.id, ...doc.data() });
      });
      isRemoteUpdate.current = true;
      setDb(prev => ({ ...prev, challenges }));
    });

    // 3. Friend Requests
    const friendReqQuery = query(
      collection(firestore, 'users', user.uid, 'friendRequests')
    );
    const unsubFriendReq = onSnapshot(friendReqQuery, (snapshot) => {
      const friendRequests: any[] = [];
      snapshot.forEach(doc => {
        friendRequests.push({ id: doc.id, ...doc.data() });
      });
      isRemoteUpdate.current = true;
      setDb(prev => ({ ...prev, friendRequests }));
    });

    return () => {
      unsubNotif();
      unsubChallenges();
      unsubFriendReq();
    };
  }, [user]);

  // REAL-TIME SYNC: Listen to User Profile (Global)
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    const userDocRef = doc(firestore, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        setUserProfile({ id: docSnap.id, ...docSnap.data() } as Player);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // ... (imports)

  // ...

  // REAL-TIME SYNC: Listen to Global Config (Permissions)
  useEffect(() => {
    // Listen even if not logged in (for Login page registration toggles)
    const configDocRef = doc(firestore, 'config', 'global');
    const unsubscribe = onSnapshot(configDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const globalConfig = docSnap.data();
        // console.log("Global Config updated from Firestore:", globalConfig);
        isRemoteUpdate.current = true;
        setDb(prev => ({ ...prev, globalConfig: globalConfig as any }));
      }
    }, (error) => {
      console.error("Error listening to Global Config:", error);
    });

    return () => unsubscribe();
  }, []); // Run once on mount (independent of user)

  // WHITE LABEL: Apply dynamic title, favicon, and theme color
  useEffect(() => {
    const wl = db.globalConfig?.whiteLabel;
    
    // Theme color injection
    let styleTag = document.getElementById('whitelabel-theme');
    if (wl?.enabled && wl.theme?.primaryColor) {
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'whitelabel-theme';
        document.head.appendChild(styleTag);
      }
      
      styleTag.innerHTML = `
        :root, html.dark {
          --color-primary: ${wl.theme.primaryColor};
        }
      `;
    } else if (styleTag) {
      styleTag.remove();
    }

    if (wl?.enabled) {
      if (wl.appName) {
        document.title = wl.appName;
      }
      if (wl.logos?.currentLogoUrl) {
        let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = wl.logos.currentLogoUrl;
      }

      // Dynamic PWA Manifest Injection
      const manifest = {
        name: wl.appName || 'Padel Brothers',
        short_name: wl.appName || 'Padel Brothers',
        start_url: '/',
        display: 'standalone',
        background_color: '#000000',
        theme_color: wl.theme?.primaryColor || '#14b8a6',
        icons: [
          {
            src: wl.logos?.appIconUrl || wl.logos?.currentLogoUrl || '/icon-192x192.png',
            sizes: '192x192',
            type: 'image/png'
          },
          {
            src: wl.logos?.appIconUrl || wl.logos?.currentLogoUrl || '/icon-512x512.png',
            sizes: '512x512',
            type: 'image/png'
          }
        ]
      };
      const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      const manifestUrl = URL.createObjectURL(manifestBlob);
      let manifestLink: HTMLLinkElement | null = document.querySelector("link[rel='manifest']");
      if (!manifestLink) {
        manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        document.head.appendChild(manifestLink);
      }
      // Revoke previous blob URL to prevent memory leak
      if (manifestLink.href && manifestLink.href.startsWith('blob:')) {
        URL.revokeObjectURL(manifestLink.href);
      }
      manifestLink.href = manifestUrl;

    } else {
      document.title = 'Padel Brothers';
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (link) {
        link.href = '/logo-no-background.svg';
      }
      let manifestLink: HTMLLinkElement | null = document.querySelector("link[rel='manifest']");
      if (manifestLink) {
        // Revoke any previous blob URL before switching back to static manifest
        if (manifestLink.href && manifestLink.href.startsWith('blob:')) {
          URL.revokeObjectURL(manifestLink.href);
        }
        manifestLink.href = '/manifest.json';
      }
    }

    // Cleanup on unmount: revoke any blob manifest URL
    return () => {
      const manifestLink: HTMLLinkElement | null = document.querySelector("link[rel='manifest']");
      if (manifestLink?.href?.startsWith('blob:')) {
        URL.revokeObjectURL(manifestLink.href);
      }
    };
  }, [db.globalConfig?.whiteLabel]);

  // REAL-TIME SYNC: Listen to Content (Titles)
  useEffect(() => {
    if (!user) return; // Guard: require auth for Firestore access
    const titlesQuery = query(collection(firestore, 'titles'), orderBy('priority', 'asc'));
    const unsubTitles = onSnapshot(titlesQuery, (snapshot) => {
      const titles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FirestoreTitleDefinition));
      isRemoteUpdate.current = true;
      setDb(prev => ({ ...prev, titles }));
    }, (error) => {
      console.error("Error listening to Titles:", error);
    });

    return () => unsubTitles();
  }, [user]);

  // REAL-TIME SYNC: Listen to Sub-collections for Current Group
  useEffect(() => {
    if (!user || !db.currentGroupId) return;
    const groupId = db.currentGroupId;

    // console.log(`Subscribing to sub-collections for group: ${groupId}`);

    // 1. Players
    const playersRef = collection(firestore, 'groups', groupId, 'players');
    const unsubPlayers = onSnapshot(playersRef, (snapshot) => {
      const players: Player[] = [];
      snapshot.forEach(doc => players.push(doc.data() as Player));

      isRemoteUpdate.current = true;
      setDb(prev => {
        const gIdx = prev.groups.findIndex(g => g.id === groupId);
        if (gIdx === -1) return prev;
        const updatedGroups = [...prev.groups];
        updatedGroups[gIdx] = { ...updatedGroups[gIdx], players };
        return { ...prev, groups: updatedGroups };
      });
    });

    // 2. Tournaments
    const tournamentsRef = collection(firestore, 'groups', groupId, 'tournaments');
    const unsubTournaments = onSnapshot(tournamentsRef, (snapshot) => {
      const tournaments: Tournament[] = [];
      snapshot.forEach(doc => tournaments.push(doc.data() as Tournament));

      isRemoteUpdate.current = true;
      setDb(prev => {
        const gIdx = prev.groups.findIndex(g => g.id === groupId);
        if (gIdx === -1) return prev;
        const updatedGroups = [...prev.groups];
        updatedGroups[gIdx] = { ...updatedGroups[gIdx], tournaments };
        return { ...prev, groups: updatedGroups };
      });
    });

    // 3. Quick Matches (Limit 1000)
    const matchesRef = collection(firestore, 'groups', groupId, 'matches');
    const qMatches = query(matchesRef, orderBy('date', 'desc'), limit(1000));
    const unsubMatches = onSnapshot(qMatches, (snapshot) => {
      const quickMatches: Match[] = [];
      snapshot.forEach(doc => quickMatches.push(doc.data() as Match));

      isRemoteUpdate.current = true;
      setDb(prev => {
        const gIdx = prev.groups.findIndex(g => g.id === groupId);
        if (gIdx === -1) return prev;
        const updatedGroups = [...prev.groups];
        updatedGroups[gIdx] = { ...updatedGroups[gIdx], quickMatches };
        return { ...prev, groups: updatedGroups };
      });
    });

    return () => {
      unsubPlayers();
      unsubTournaments();
      unsubMatches();
    };
  }, [user, db.currentGroupId]);

  // Sync data to Firestore when user is authenticated (Write)
  // WARNING: This aggressive auto-sync was causing 'resource-exhausted' errors in Firestore.
  // In V2 architecture, data is updated directly via specific interactions (e.g. creating a match writes to the match collection).
  // The global 'database' backup is disabled to prevent unlimited writes.
  useEffect(() => {
    if (!user || isLoadingData) return;

    const syncToFirestore = async () => {
      // Prevent infinite loop: If this update came from Firestore, DO NOT write it back
      if (isRemoteUpdate.current) {
        // console.log("Skipping Firestore write (Remote Update)");
        isRemoteUpdate.current = false;
        return;
      }

      try {
        // 1. Save user's database to Firestore (Backup/Legacy)
        // EXCLUDE globalConfig from backup to prevent overwriting live config with stale data
        // EXCLUDE globalConfig from backup to prevent overwriting live config with stale data

        // --- DISABLE AGGRESSIVE BACKUP TO PREVENT QUOTA EXHAUSTION ---
        // const userDocRef = doc(firestore, 'userDatabases', user.uid);
        // await setDoc(userDocRef, {
        //   database: sanitizeForFirestore(dbToSave),
        //   lastUpdated: new Date().toISOString(),
        // }, { merge: true });

        // 2. Sync Groups to Shared Collection
        // This ensures other users (and Admin) see the updates
        // DO NOT WRITE THE WHOLE GROUP DOCUMENT REPEATEDLY IF WE ARE USING SUBCOLLECTIONS 
        // AND ONLY LISTENING - writes should happen on explicit actions (e.g. edit group settings)

        // --- DISABLE AGGRESSIVE GROUP WRITE LOOP ---
        /*
        const groupsRef = collection(firestore, 'groups');
        for (const group of db.groups) {
          const groupDoc = doc(groupsRef, group.id);
          // Add 'members' field for querying
          // EXCLUDE large arrays from the parent document write (Migration V2)
          const { players, quickMatches, tournaments, ...groupMetadata } = group;

          let groupData: any = {
            ...groupMetadata,
            lastUpdated: new Date().toISOString()
          };

          // ONLY update derived counts if we have the full data loaded (Current Group)
          // Otherwise, we risk overwriting with empty arrays
          if (group.id === db.currentGroupId) {
            groupData.members = (group.players || []).map(p => p.id);
            groupData.tournamentCount = (group.tournaments || []).length;
          }

          // Check permissions before writing to group document
          const isGroupAdmin = group.ownerId === user.uid || (group.players || []).some((p: any) => p.id === user.uid && p.role === 'admin');

          if (isGroupAdmin) {
            await setDoc(groupDoc, sanitizeForFirestore(groupData), { merge: true });
          }
        }
        */

      } catch (error) {
        console.error('Error syncing to Firestore:', error);
      }
    };

    // Debounce: save to Firestore 1 second after last change
    const timeoutId = setTimeout(syncToFirestore, 1000);
    return () => clearTimeout(timeoutId);
  }, [db, user, isLoadingData]);

  // Auto-save to localStorage whenever DB changes (Lightweight: only save data actually used on reload)
  // The load function (L148) already EXCLUDES: globalConfig, groups, currentGroupId, users,
  // notifications, challenges, friendRequests, globalClubs — so serializing them is pure waste.
  useEffect(() => {
    if (!isLoadingData) {
      // Debounce to avoid CPU spikes from rapid state changes
      const timeoutId = setTimeout(() => {
        // Only save the minimal data that survives a reload
        const { globalConfig, groups, currentGroupId, users, notifications, challenges, friendRequests, globalClubs, titles, ...dataToSave } = db;
        try {
          localStorage.setItem('padelBrothersDB_v15', JSON.stringify(dataToSave));
        } catch (e) {
          // Silently handle QuotaExceededError instead of crashing
          console.warn('[AppContext] localStorage save failed (quota?):', e);
        }
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [db, isLoadingData]);

  // Load data from Firestore when user logs in AND sync user profile to players list
  useEffect(() => {
    if (!user) return;

    const loadFromFirestore = async () => {
      try {
        const userDocRef = doc(firestore, 'userDatabases', user.uid);
        const docSnap = await getDoc(userDocRef);

        let currentDb = db;

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data && data.database) {
            const firestoreDb = data.database as Database;
            // Use Firestore data ONLY if local data is empty to preserve "current state"
            if (firestoreDb.groups.length > 0 && db.groups.length === 0) {
              // EXCLUDE all real-time managed data from legacy backup
              // - globalConfig: live config listener
              // - groups: live groups listener (stale backup causes ghost groups)
              // - currentGroupId: must come from URL only (prevents auto-nav to wrong group)
              // - users: live users listener (stale list causes wrong member counts)
              // - notifications/challenges/friendRequests: live listeners (stale backup causes ghost items)
              // - globalClubs: live listener (stale backup persists deleted clubs)
              const { 
                globalConfig, 
                groups, 
                currentGroupId: _staleGroupId, 
                users: _staleUsers, 
                notifications: _staleNotifs,
                challenges: _staleChallenges,
                friendRequests: _staleFriendReqs,
                globalClubs: _staleClubs,
                ...dbToLoad 
              } = firestoreDb as any;

              setDb(prev => ({ ...prev, ...dbToLoad }));
              currentDb = { ...currentDb, ...dbToLoad };
              // localStorage auto-save is handled by the debounced effect above
            }
          }
        }

        // --- SYNC USER TO PLAYERS LIST ---
        if (userData && (userData.pseudo || userData.firstName)) {
          setDb(prevDb => {
            let hasChanges = false;
            const updatedGroups = prevDb.groups.map(group => {
              if (!group.players) return group; // Safety check
              const playerIndex = group.players.findIndex(p => p.id === user.uid);
              if (playerIndex !== -1) {
                const currentPlayer = group.players[playerIndex];
                const isDifferent =
                  currentPlayer.name !== (userData.displayName || currentPlayer.name) ||
                  currentPlayer.pseudo !== userData.pseudo ||
                  currentPlayer.firstName !== userData.firstName ||
                  currentPlayer.lastName !== userData.lastName ||
                  currentPlayer.playStyle !== userData.playStyle ||
                  currentPlayer.preferredSide !== userData.preferredSide ||
                  currentPlayer.avatar !== (userData.photoURL || currentPlayer.avatar) ||
                  currentPlayer.city !== userData.city ||
                  currentPlayer.department !== userData.department ||
                  currentPlayer.clubId !== userData.clubId ||
                  currentPlayer.bio !== userData.bio ||
                  currentPlayer.partnerSearch !== userData.partnerSearch;

                if (isDifferent) {
                  hasChanges = true;
                  const updatedPlayers = [...group.players];
                  updatedPlayers[playerIndex] = {
                    ...currentPlayer,
                    name: userData.displayName || currentPlayer.name,
                    pseudo: userData.pseudo,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    playStyle: userData.playStyle,
                    preferredSide: userData.preferredSide,
                    avatar: userData.photoURL || currentPlayer.avatar,
                    city: userData.city,
                    department: userData.department,
                    clubId: userData.clubId,
                    bio: userData.bio,
                    partnerSearch: userData.partnerSearch,
                    lastProfileEdit: userData.lastProfileEdit
                  };
                  return { ...group, players: updatedPlayers };
                }
              }
              return group;
            });

            if (hasChanges) {
              // console.log("Synced user profile to groups.");
              const newDb = { ...prevDb, groups: updatedGroups };
              // localStorage auto-save is handled by the debounced effect above
              return newDb;
            }
            return prevDb;
          });
        }

      } catch (error) {
        console.error('Error loading from Firestore:', error);
      }
    };

    loadFromFirestore();
    loadFromFirestore();
  }, [user, userData]);

  // REAL-TIME SYNC: Update groups.players when global users list changes
  // This fixes the issue where profile pictures/names are not updated in the UI
  // because the UI reads from group.players which is a snapshot, not a live reference to users
  useEffect(() => {
    if (!db.users || db.users.length === 0 || !db.groups || db.groups.length === 0) return;

    setDb(prevDb => {
      let hasChanges = false;
      const updatedGroups = prevDb.groups.map(group => {
        if (!group.players) return group;

        let groupHasChanges = false;
        const updatedPlayers = group.players.map(player => {
          const latestUser = prevDb.users?.find(u => u.id === player.id);

          if (latestUser) {
            // Check for differences in key display fields
            const isDifferent =
              player.avatar !== latestUser.avatar ||
              player.pseudo !== latestUser.pseudo ||
              player.firstName !== latestUser.firstName ||
              player.lastName !== latestUser.lastName ||
              player.name !== (latestUser.displayName || latestUser.name); // Use displayName if available

            if (isDifferent) {
              groupHasChanges = true;
              return {
                ...player,
                avatar: latestUser.avatar,
                pseudo: latestUser.pseudo,
                firstName: latestUser.firstName,
                lastName: latestUser.lastName,
                name: latestUser.displayName || latestUser.name || player.name
              };
            }
          }
          return player;
        });

        if (groupHasChanges) {
          hasChanges = true;
          // console.log(`[Sync] Updated players in group ${group.id} from global users list.`);
          return { ...group, players: updatedPlayers };
        }
        return group;
      });

      if (hasChanges) {
        return { ...prevDb, groups: updatedGroups };
      }
      return prevDb;
    });
  }, [db.users]); // Dependency on db.users ensures this runs whenever global users list updates

  // Check for expired challenges
  useEffect(() => {
    if (isLoadingData || !db.groups.length) return;

    const newDb = checkChallengeDeadlines(db);
    if (newDb !== db) {
      setDb(newDb);
      // console.log("Expired challenges processed.");
    }
  }, [db, isLoadingData]);

  // --- OFFICIAL GROUP LOGIC ---


  // Legacy auto-join logic removed. 
  // Group joining is now handled by the 'onUserCreated' Cloud Function to ensure data consistency and security.
  // The client relies on the real-time Firestore listener (lines 193-228) to receive the group once added.


  const saveData = () => {
    // Only save the minimal data that survives a reload (exclude real-time managed data)
    const { globalConfig, groups, currentGroupId, users, notifications, challenges, friendRequests, globalClubs, titles, ...dataToSave } = db;
    try {
      localStorage.setItem('padelBrothersDB_v15', JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('[AppContext] localStorage save failed (quota?):', e);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    const toastContainer = document.getElementById('toast-container');
    if (toastContainer) {
      const toast = document.createElement('div');

      let bgColor = 'bg-surface-2';
      let textColor = 'text-text-primary';
      let borderColor = 'border-glass-border';

      if (type === 'success') {
        bgColor = 'bg-success/20';
        textColor = 'text-success';
        borderColor = 'border-success';
      } else if (type === 'error') {
        bgColor = 'bg-danger/20';
        textColor = 'text-danger';
        borderColor = 'border-danger';
      } else if (type === 'warning') {
        bgColor = 'bg-warning/20';
        textColor = 'text-warning';
        borderColor = 'border-warning';
      }

      toast.className = `p-4 rounded-lg shadow-lg mb-2 flex items-center justify-between animate-toastIn border ${borderColor} ${bgColor} ${textColor} backdrop-blur-md`;
      toast.innerHTML = `
        <span class="font-medium">${message}</span>
      `;

      toastContainer.appendChild(toast);

      setTimeout(() => {
        toast.classList.remove('animate-toastIn');
        toast.classList.add('animate-toastOut');
        setTimeout(() => {
          toast.remove();
        }, 500);
      }, 3000);
    }
  };

  const rawCurrentGroup = db.groups?.find(g => g.id === db.currentGroupId) || null;

  const currentGroup = React.useMemo(() => {
    if (!rawCurrentGroup) return null;
    if (!db.users || db.users.length === 0) return rawCurrentGroup;

    // Filter out deleted users and enrich with latest global data (avatar, name)
    const validUserIds = new Set((db.users || []).map((u: any) => u.id));
    const validPseudos = new Set((db.users || []).map((u: any) => u.pseudo?.toLowerCase()).filter(Boolean));

    const enrichedPlayers = (rawCurrentGroup.players || [])
      .filter(p => {
        // A player is valid if their ID is in users OR if their pseudo is in users (for manual players)
        if (validUserIds.has(p.id)) return true;
        if (p.pseudo && validPseudos.has(p.pseudo.toLowerCase())) return true;
        return false;
      })
      .map(p => {
        // Find global user by ID first, then by pseudo as fallback
        let globalUser = (db.users || []).find((u: any) => u.id === p.id);
        if (!globalUser && p.pseudo) {
          globalUser = (db.users || []).find((u: any) => u.pseudo?.toLowerCase() === p.pseudo?.toLowerCase());
        }

        if (!globalUser) return p;

        return {
          ...p,
          avatar: globalUser.photoURL || globalUser.avatar || p.avatar,
          pseudo: globalUser.pseudo || p.pseudo || p.name,
          name: globalUser.displayName || globalUser.pseudo || globalUser.name || p.name,
          firstName: globalUser.firstName,
          lastName: globalUser.lastName,
          // Also sync global rating for consistency in dropdowns
          rating: globalUser.rating ?? p.rating ?? 1500,
          rd: globalUser.rd ?? p.rd ?? 350,
          elo: globalUser.rating ? Math.round(globalUser.rating / 1.5) : (p.elo || 1000)
        };
      });

    return {
      ...rawCurrentGroup,
      players: enrichedPlayers
    };
  }, [rawCurrentGroup, db.users]);

  const setCurrentGroup = (id: string | null) => {
    setDb(prev => {
      if (prev.currentGroupId === id) return prev;
      return { ...prev, currentGroupId: id };
    });
  };

  const goToPage = (view: View, data: any = {}) => {
    setContextData(data);

    // If navigating to a group page, ensure we have a group selected
    // Prioritize data.groupId (for immediate navigation after selection) over currentGroup.id
    const groupId = data.groupId || currentGroup?.id || 'official_padel_brothers';

    // Eagerly sync the group context before routing to eliminate flickering/null renders
    if (data.groupId && db.currentGroupId !== groupId) {
      setCurrentGroup(groupId);
    }

    switch (view) {
      case View.GROUP_SELECT:
        navigate('/groups');
        break;
      case View.HOME:
        navigate(`/group/${groupId}`);
        break;
      case View.TOURNAMENTS:
        navigate(`/group/${groupId}/tournaments`);
        break;
      case View.TOURNAMENT_DETAIL:
        // Expect data to contain tournamentId, optionally initialTab
        if (data.tournamentId) {
          const tabParam = data.initialTab ? `?tab=${data.initialTab}` : '';
          navigate(`/group/${groupId}/tournament/${data.tournamentId}${tabParam}`);
        } else {
          navigate(`/group/${groupId}/tournaments`);
        }
        break;
      case View.PLAYERS:
        navigate(`/group/${groupId}/players`);
        break;
      case View.PLAYER_DETAIL:
        if (data.playerId) {
          navigate(`/group/${groupId}/player/${data.playerId}`);
        } else {
          navigate(`/group/${groupId}/players`);
        }
        break;
      case View.CLUBS:
        navigate('/clubs');
        break;
      case View.H2H:
        navigate(`/group/${groupId}/h2h`);
        break;
      case View.MATCHMAKING:
        navigate(`/group/${groupId}/matchmaking`);
        break;
      case View.BRACKET:
        if (data.tournamentId) {
          navigate(`/group/${groupId}/tournament/${data.tournamentId}/bracket`);
        }
        break;
      case View.CHALLENGES:
        navigate('/challenges', { state: data });
        break;
      case View.ADMIN:
        navigate('/admin');
        break;
      case View.PROFILE:
        // Support viewing other users' profiles
        if (data?.userId) {
          navigate(`/profile/${data.userId}`);
        } else {
          navigate('/profile');
        }
        break;
      case View.INBOX:
        navigate('/inbox');
        break;
      case View.RULES:
        navigate('/rules');
        break;
      case View.CREATE_TOURNAMENT:
        navigate(`/group/${groupId}/create-tournament`);
        break;
      case View.CREATE_TEAM:
        navigate(`/group/${groupId}/create-team${data.tournamentId ? `?tournamentId=${data.tournamentId}` : ''}`);
        break;
      case View.CREATE_MATCH:
        navigate(`/group/${groupId}/create-match${data.tournamentId ? `?tournamentId=${data.tournamentId}` : ''}`);
        break;
      case View.KOTC_DETAIL:
        if (data.kotcId) {
          navigate(`/group/${groupId}/kotc/${data.kotcId}`);
        }
        break;
      case View.HUB:
        navigate('/');
        break;
      case View.DASHBOARD:
        navigate('/dashboard');
        break;
      case View.FRIENDS:
        navigate('/friends');
        break;
      case View.GLOBAL_MATCHES:
        navigate('/matches');
        break;
      case View.GLOBAL_TOURNAMENTS:
        navigate('/my-tournaments');
        break;
      case View.SETTINGS:
        navigate('/settings');
        break;
      case View.FULL_FEED:
        navigate(`/group/${groupId}/feed`);
        break;
      case View.EDIT_GROUP:
        navigate(`/group/${groupId}/edit`);
        break;
      case View.SUBSCRIPTION:
        navigate('/subscription');
        break;
      case View.GROUP_ADMIN:
        navigate(`/group/${groupId}/admin`);
        break;
      case View.GROUP_CHAT:
        navigate(`/group/${groupId}/chat`);
        break;
      case View.SCORE:
        navigate('/score');
        break;
      default:
        navigate('/');
    }
    window.scrollTo(0, 0);
  };

  const applyTheme = (newTheme: 'standard' | 'light' | 'pro') => {
    const html = document.documentElement;
    html.classList.remove('dark', 'theme-pro');

    if (newTheme === 'standard') {
      html.classList.add('dark');
    } else if (newTheme === 'pro') {
      html.classList.add('theme-pro');
    }
    // light is default (no class)
  };

  const toggleTheme = (newTheme: 'standard' | 'light' | 'pro') => {
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('padelAppTheme', newTheme);
  };

  const contextValue = React.useMemo(() => {
    let activeGroup = currentGroup;
    
    // Inject test bots (dummyPlayers) from all tournaments globally so they appear everywhere
    if (activeGroup && activeGroup.tournaments && activeGroup.players) {
        const allDummies = activeGroup.tournaments.flatMap(t => t.dummyPlayers || []);
        if (allDummies.length > 0) {
            const mergedPlayers = [
                ...activeGroup.players,
                ...allDummies
            ].filter((p, index, self) => index === self.findIndex(t => t.id === p.id));
            
            activeGroup = { ...activeGroup, players: mergedPlayers };
        }
    }

    return {
      db,
      view,
      currentGroup: activeGroup,
      setCurrentGroup,
      setDb,
      saveData,
      showToast,
      contextData,
      setContextData,
      goToPage,
      theme,
      toggleTheme,
      userProfile,
      matchToShare,
      setMatchToShare,
      shareMatch,
      globalRegionOverride,
      setGlobalRegionOverride,
      isMenuOpen,
      setIsMenuOpen
    };
  }, [db, view, currentGroup, contextData, theme, userProfile, isLoadingData, matchToShare, globalRegionOverride, isMenuOpen, setIsMenuOpen]); // Added dependencies

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
